import { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';

/**
 * Custom hook to manage score editing for admin users
 * @param {Array} matches - Array of match objects
 * @returns {Object} Scores state and handlers
 */
export const useScoresManager = (matches) => {
  const [adminMode, setAdminMode] = useState(false);
  const [editableScores, setEditableScores] = useState({});
  const [originalScores, setOriginalScores] = useState({});
  const [saving, setSaving] = useState(false);

  // Extract existing scores from matches
  useEffect(() => {
    const existingScores = {};
    matches.forEach((match) => {
      if (match.homeScore !== null || match.awayScore !== null) {
        existingScores[match.id] = {
          homeScore: match.homeScore,
          awayScore: match.awayScore,
        };
      }
    });
    setEditableScores(existingScores);
    setOriginalScores(JSON.parse(JSON.stringify(existingScores)));
  }, [matches]);

  /**
   * Handle score change
   * @param {number} matchId - Match ID
   * @param {string} scoreType - 'homeScore' or 'awayScore'
   * @param {string} value - Score value
   */
  const handleScoreChange = (matchId, scoreType, value) => {
    const numValue = value === '' ? null : parseInt(value, 10);
    setEditableScores((prev) => ({
      ...prev,
      [matchId]: {
        ...prev[matchId],
        [scoreType]: numValue,
      },
    }));
  };

  /**
   * Check if scores have changed from original
   * @returns {boolean} True if scores have changed
   */
  const hasScoreChanges = () => {
    const currentScoreKeys = Object.keys(editableScores);
    const originalScoreKeys = Object.keys(originalScores);

    if (currentScoreKeys.length !== originalScoreKeys.length) {
      return true;
    }

    return currentScoreKeys.some((matchId) => {
      const current = editableScores[matchId];
      const original = originalScores[matchId];
      return (
        !original ||
        current.homeScore !== original.homeScore ||
        current.awayScore !== original.awayScore
      );
    });
  };

  /**
   * Save all scores
   * @param {Array} matches - Array of match objects for validation
   * @returns {Promise<Object>} Result with summary and any errors
   */
  const saveAllScores = async (matches) => {
    setSaving(true);
    try {
      // Check for incomplete scores
      const incompleteMatches = [];
      const completeScores = [];

      Object.entries(editableScores).forEach(([matchId, scores]) => {
        const hasHomeScore = scores.homeScore !== null && scores.homeScore !== undefined;
        const hasAwayScore = scores.awayScore !== null && scores.awayScore !== undefined;

        if (hasHomeScore && hasAwayScore) {
          completeScores.push({
            matchId: parseInt(matchId),
            homeScore: scores.homeScore,
            awayScore: scores.awayScore,
          });
        } else if (hasHomeScore || hasAwayScore) {
          const match = matches.find((m) => m.id === parseInt(matchId));
          if (match) {
            incompleteMatches.push(
              `${match.homeTeam.name} vs ${match.awayTeam?.name || 'BYE'} (${hasHomeScore ? 'missing away score' : 'missing home score'})`
            );
          }
        }
      });

      if (incompleteMatches.length > 0) {
        throw new Error(`Please enter both scores for: ${incompleteMatches.join(', ')}`);
      }

      if (completeScores.length === 0) {
        throw new Error('No scores to save. Please enter scores for at least one match.');
      }

      // Use batch endpoint
      const response = await adminAPI.batchEnterScores(completeScores);

      // Update original scores to reflect saved state
      setOriginalScores({ ...editableScores });

      return {
        success: true,
        summary: response.data.summary,
        results: response.data.results,
        completeScores,
      };
    } catch (err) {
      throw err;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle match status change
   * @param {number} matchId - Match ID
   * @param {string} newStatus - New status
   * @returns {Promise<void>}
   */
  const handleStatusChange = async (matchId, newStatus) => {
    if (newStatus === 'live') {
      await adminAPI.markMatchLive(matchId);
    } else if (newStatus === 'awaiting_scores') {
      await adminAPI.markMatchFinished(matchId);
    } else if (newStatus === 'cancelled') {
      await adminAPI.cancelMatch(matchId);
    }

    return { matchId, newStatus };
  };

  return {
    adminMode,
    editableScores,
    originalScores,
    saving,
    setAdminMode,
    setEditableScores,
    setOriginalScores,
    handleScoreChange,
    hasScoreChanges,
    saveAllScores,
    handleStatusChange,
  };
};
